/**
 * ホスティング（M3）。有効な版・公開 URL・デプロイ一覧と、任意の ready 版への即時切り替え（= ロールバック）。
 * 切り替えは hosting モジュールの activate を呼ぶだけで、ファイルの再アップロードは無い（版は不変）。
 * 独自ドメイン（play.mygame.com）の一覧・追加・削除は components/HostingDomains.tsx（2026-09-27）。
 * URL スラッグの変更・古いスラッグの転送は components/SlugSettings.tsx（2026-09-28 creator/slug-rename）。公開 URL の直下 = 独自ドメインの前に置く
 * （スラッグは公開 URL そのもの・どちらの環境でも同じ値）。
 * プロジェクト名の変更は components/ProjectNameCard.tsx（2026-09-28 creator/project-rename・AGT-10）。スラッグのカードの直前に置く
 * （名前 = メールの件名・差出人に出る / スラッグ = URL に出る、を並べて見せる。ゲームに「設定」の画面が無いのでここ）。
 */
import { useEffect, useState } from 'react';
import { api, ApiError, envPath, type Deployment, type HostingView } from '../api';
import { ago, bytes, ConfirmDialog, Empty, ErrorState, fullDate, PageHeader, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, translate, useT, type MessageKey } from '../i18n';
import { useShell } from './ProjectShell';
import { CLI } from '../cliCommand';
import { HostingDomains } from '../components/HostingDomains';
import { SlugSettings } from '../components/SlugSettings';
import { ProjectNameCard } from '../components/ProjectNameCard';
import { projectEyebrow } from '../projectNav';
import { OwnerOnly, OwnerOnlyNote, useIsOwner } from '../components/OwnerOnly';

const STATUS_KEYS: Record<string, MessageKey> = {
  pending: 'deploy.status.pending',
  uploading: 'deploy.status.uploading',
  ready: 'deploy.status.ready',
  failed: 'deploy.status.failed'
};
/** デプロイの状態。未知の状態（hosting モジュールが足したもの）はそのまま出す */
const statusLabel = (st: string) => (STATUS_KEYS[st] ? translate(STATUS_KEYS[st]) : st);

export function HostingPage() {
  const { project, env, reloadProject } = useShell();
  const isOwner = useIsOwner();
  const base = envPath(project.id, env);
  const r = useResource<HostingView>(`${base}/hosting`);
  const [target, setTarget] = useState<Deployment | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // 通知は版番号だけ持ち、表示時に翻訳する（言語を切り替えても今の言語で出る）
  const [notice, setNotice] = useState<number | null>(null);
  const { t, tr } = useT();
  const envName = t(`env.inline.${env}`);
  // 環境を切り替えたら前の環境の「vN is now live」を残さない
  useEffect(() => setNotice(null), [base]);

  const activate = async () => {
    if (!target) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`${base}/deployments/${target.id}/activate`, { method: 'POST', body: {} });
      setNotice(target.version);
      setTarget(null);
      r.reload();
    } catch (e) {
      const ae = e as ApiError;
      setErr(ae.notAvailable ? t('hosting.rollbackUnavailable') : errorText(ae));
    } finally {
      setBusy(false);
    }
  };

  // CLI の deploy は既定 production（packages/cli）。development へは --env を明示する
  const deployCmd = env === 'production' ? `${CLI} deploy ./dist` : `${CLI} deploy ./dist --env development`;
  const current = r.data?.active;

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('hosting.title')} />
      {/* 共同作業者が公開中のアプリを新しくすると持ち主にメールが届く（founder 10/21 決定 2 案 A・サーバーの hosting/memberPublishNotice.ts）= 先に言っておく */}
      {!isOwner && env === 'production' && (
        <p className="alert alert-warn" role="status">
          {t('members.publishNotice')}
        </p>
      )}
      {r.loading && !r.data && <Spinner />}
      {r.error && <ErrorState error={r.error} what={t('hosting.title')} onRetry={r.reload} />}
      {notice !== null && (
        <div className="alert alert-ok" role="status">
          {t('hosting.nowLive', { version: String(notice) })}
        </div>
      )}
      {r.data && (
        <>
          <section className="card live-card">
            <div>
              <span className="sidebar-label">{t('hosting.liveUrl')}</span>
              {r.data.url ? (
                <a className="live-url" href={r.data.url} target="_blank" rel="noreferrer noopener">
                  {r.data.url} ↗
                </a>
              ) : (
                <p className="muted">{t('hosting.noUrl')}</p>
              )}
            </div>
            <div className="live-version">
              {current ? (
                <>
                  <span className="live-v">v{current.version}</span>
                  <span className="small muted">
                    {t('hosting.liveSince', { ago: ago(current.activatedAt) })} · {t('hosting.files', { count: current.fileCount })} · {bytes(current.totalBytes)}
                  </span>
                </>
              ) : (
                <span className="muted">{t('hosting.nothingLive')}</span>
              )}
            </div>
          </section>

          {/* プロジェクト名（2026-09-28 creator/project-rename・AGT-10）。変えたら枠を読み直す = サイドバーの名前が新しくなる */}
          {/* 名前・URL・独自ドメインの変更は持ち主だけ（2026-10-22 core/handoff-1b・共同作業者には出さない = 押して 403 owner_only を見せない） */}
          <OwnerOnly fallback={<OwnerOnlyNote />}>
          <ProjectNameCard projectId={project.id} name={project.name} onRenamed={reloadProject} />

          {/* URL スラッグ（2026-09-28 creator/slug-rename）。変えたらサイドバーのスラッグと公開 URL を読み直す */}
          <SlugSettings
            projectId={project.id}
            onChanged={() => {
              reloadProject();
              r.reload();
            }}
          />

          {/* 独自ドメイン（production だけ・2026-09-27 creator/custom-domains）。公開 URL の直下 = 「どこで遊べるか」をまとめて見せる */}
          <HostingDomains projectId={project.id} env={env} onChanged={r.reload} />
          </OwnerOnly>

          {r.data.deployments.length === 0 ? (
            // 空の状態（2026-09-30 console/core-first C3）: まず AI エージェントに「公開して」と頼む道（MASTER-PLAN §0: 開発用に出てから
            // 会話で 1 回「本番にも出す？」）、その下に CLI の 1 行を小さく。以前は CLI の 1 行だけ = 自分で打つ前提だった
            <Empty title={t('hosting.emptyTitle')}>
              <p>{t('hosting.empty.ask')}</p>
              <p className="small muted">{tr('hosting.empty.cli', { cmd: <code>{deployCmd}</code> })}</p>
            </Empty>
          ) : (
            <section className="card">
              <h2 className="card-title">{t('hosting.deployments')}</h2>
              <ol className="timeline">
                {r.data.deployments.map((d) => (
                  <li key={d.id} className={`tl ${d.active ? 'tl-active' : ''} tl-${d.status}`}>
                    <span className="tl-dot" aria-hidden="true" />
                    <div className="tl-main">
                      <div className="tl-head">
                        <strong>v{d.version}</strong>
                        {d.active && <span className="pill pill-accent">{t('hosting.livePill')}</span>}
                        {d.status !== 'ready' && <span className={`pill ${d.status === 'failed' ? 'pill-danger' : ''}`}>{statusLabel(d.status)}</span>}
                        <span className="muted small" title={fullDate(d.createdAt)}>
                          {ago(d.createdAt)}
                        </span>
                      </div>
                      <div className="small">{d.message ?? <span className="muted">{t('hosting.noMessage')}</span>}</div>
                      <div className="small muted">
                        {t('hosting.files', { count: d.fileCount })} · {bytes(d.totalBytes)} · {t(d.createdBy.startsWith('key:') ? 'hosting.byKey' : 'hosting.byDeveloper')}
                        {d.failureReason && ` · ${d.failureReason}`}
                      </div>
                    </div>
                    {d.status === 'ready' && !d.active && (
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTarget(d)}>
                        {current && d.version < current.version ? t('hosting.rollBack') : t('hosting.makeLive')}
                      </button>
                    )}
                  </li>
                ))}
              </ol>
            </section>
          )}
        </>
      )}
      <ConfirmDialog
        open={!!target}
        title={t('hosting.confirmTitle', { version: String(target?.version ?? ''), env: envName })}
        confirmLabel={t('hosting.confirmLabel', { version: String(target?.version ?? '') })}
        tone={env === 'production' ? 'danger' : 'default'}
        busy={busy}
        onClose={() => {
          setTarget(null);
          setErr(null);
        }}
        onConfirm={() => void activate()}
      >
        <p>{t('hosting.confirmBody', { version: String(target?.version ?? '') })}</p>
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
